import * as React from 'react'
import { connect } from 'react-redux'
import { FormValues } from '@imbox/form-renderer/lib/types/form-value'

import {
  getForm,
  navigateToThankYou,
  submitCustomForm,
  SubmitFormResult,
} from '../../actions/forms'
import { FormBuilder } from '../../lib/form-builder/formbuilder-loadable'
import { strings } from '../../lib/localization'
import { HubItem } from '../../reducers/hub'
import { formSelector } from '../../selectors/forms'
import { currentHubSelector, hubSelector } from '../../selectors/hub'
import { companyNameSelector, consentIdSelector } from '../../selectors/widget'
import { RootState } from '../../types/rootstate'
import { BrandingIcon } from '../branding'
import { Container } from '../container'
import { Header } from './header'

import '../../sass/forms/main.scss'
import '../../sass/forms.scss'

interface StateProps {
  form: RootState['forms']['key']
  agentId: number
  companyName: string
  consentId: number
  hub: HubItem[]
  currentHub: HubItem
}

interface DispatchProps {
  submitCustomForm: (
    userId: number,
    instanceId: string,
    formId: string,
    agentId: number,
    channelId: string,
    values: FormValues,
    consentId: number,
    fileTokens: string[]
  ) => Promise<SubmitFormResult>

  getForm: (userId: number, agentId: number, formId: number) => void
  navigateToThankYou: (formId: number) => void
}

interface OwnProps {
  params
}

type MainProps = StateProps & DispatchProps & OwnProps

function mapStateToProps(state: RootState, ownProps: OwnProps): StateProps {
  return {
    form: formSelector(state, ownProps.params.formId),
    agentId: state.widget.agentId,
    companyName: companyNameSelector(state),
    consentId: consentIdSelector(state),
    hub: hubSelector(state),
    currentHub: currentHubSelector(state),
  }
}

function mapDispatchToProps(dispatch): DispatchProps {
  return {
    submitCustomForm: (
      userId,
      instanceId,
      formId,
      agentId,
      channelId,
      values,
      consentId,
      fileTokens
    ): Promise<SubmitFormResult> =>
      dispatch(
        submitCustomForm(
          userId,
          instanceId,
          formId,
          agentId,
          channelId,
          values,
          consentId,
          fileTokens
        )
      ),
    getForm: (userId, agentId, formId): void =>
      dispatch(getForm(userId, null, formId)),
    navigateToThankYou: (formId): void => dispatch(navigateToThankYou(formId)),
  }
}

interface MainState {
  isSubmitting: boolean
  hasError: boolean
  reason: string
}

class MainComponent extends React.Component<MainProps, MainState> {
  references: {
    container: HTMLDivElement
  } = {
    container: null,
  }

  constructor(props) {
    super(props)

    this.state = {
      isSubmitting: false,
      hasError: false,
      reason: '',
    }
  }

  componentDidMount(): void {
    if (!this.props.form && this.props.params.userId) {
      this.props.getForm(
        this.props.params.userId,
        this.props.agentId,
        this.props.params.formId
      )
    }
  }

  componentDidUpdate(): void {
    if (!this.props.form && this.props.params.userId) {
      this.props.getForm(
        this.props.params.userId,
        this.props.agentId,
        this.props.params.formId
      )
    }
  }

  componentWillReceiveProps(props): void {
    if (this.props.params.formId !== props.params.formId) {
      this.props.getForm(
        this.props.params.userId,
        this.props.agentId,
        props.params.formId
      )
    }
  }

  handleSubmit = (values: FormValues, fileTokens: string[]): void => {
    if (!this.state.isSubmitting) {
      this.setState({
        isSubmitting: true,
      })
      this.props
        .submitCustomForm(
          this.props.params.userId,
          this.getFormInstanceId(),
          this.props.params.formId,
          null,
          null,
          values,
          this.props.consentId,
          fileTokens
        )
        .then(() => {
          this.props.navigateToThankYou(this.props.params.formId)
          this.setState({
            isSubmitting: false,
          })
        })
        .catch(() => {
          this.setState({
            isSubmitting: false,
            hasError: true,
            reason: strings.formUnknownError,
          })
        })
    }
  }

  getFormInstanceId(): string {
    if (this.props.currentHub) {
      return this.props.currentHub.id
    } else {
      return ''
    }
  }

  render(): JSX.Element {
    const hasForm = this.props.form && this.props.form.status === 'success'
    const hasFooter =
      hasForm &&
      this.props.form.form.some(
        (form) => form.field_type === 'formButton' && form.sticky === '1'
      )
    const extraCssClasses = hasFooter ? ['has-footer'] : []

    return (
      <Container name="forms" extraCssClasses={extraCssClasses}>
        <Header />
        <div className="imbox-forms-wrapper">
          <div className="container">
            <div className="row">
              {hasForm && (
                <FormBuilder
                  form={this.props.form.form}
                  instanceId={this.getFormInstanceId()}
                  formId={this.props.params.formId}
                  companyName={this.props.companyName}
                  onSubmit={this.handleSubmit}
                />
              )}
              {this.state.hasError && (
                <div className="col-xs-12">{this.state.reason}</div>
              )}
            </div>
          </div>
          <BrandingIcon type="form" />
        </div>
      </Container>
    )
  }
}

const Main = connect<StateProps, DispatchProps, OwnProps>(
  mapStateToProps,
  mapDispatchToProps
)(MainComponent)

export { Main }
